import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory, Link } from 'react-router';
import Helmet from 'react-helmet';
import moment from 'moment-timezone';
import { Field, reduxForm, propTypes as formProps } from 'redux-form';
import { MinimalFooter } from '@zola/zola-ui';
import { checkEmail } from '@zola-helpers/client';

import { RenderField, RenderSubmitButton, RenderHiddenField } from '../../components/common/form';
import { signup } from '../../actions/AuthActions';
import { identifyGuestUser } from '../../actions/UserActions';
import FacebookLoginButton from '../../components/common/auth/FacebookLoginButton';
import * as Validations from '../../components/common/form/commonValidations';

import '../../stylesheets/pages/preauth/SignupPageContainer.less';

class SignupFullPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      errorMessage: '',
      skipRender: false
    };

    this.handleSignup = this.handleSignup.bind(this);
    this.handleError = this.handleError.bind(this);
    this.mapBusinessUnit = this.mapBusinessUnit.bind(this);
    this.onSignupSuccess = this.onSignupSuccess.bind(this);
  }

  componentDidMount() {
    const { businessUnit } = this.props;
    if (businessUnit) {
      const businessResource = this.mapBusinessUnit(businessUnit);
      if (businessResource === 'WEDDING_REGISTRY') {
        // We should never get here but redirect just in case.
        window.location.href = '/onboard/new';
      }
    } else {
      // If the user reaches this page without selecting a product, redirect to select-product page.
      browserHistory.push('/onboard/select-product');
    }
  }

  onSignupSuccess() {
    const { businessUnit } = this.props;
    browserHistory.push(`/wedding/onboard?product=${businessUnit}`);
  }

  mapBusinessUnit(businessUnit) {
    switch (businessUnit) {
      case 'REGISTRY': return 'WEDDING_REGISTRY';
      case 'WEDDING_WEBSITE':
      case 'WEDDING_CHECKLIST':
        return 'WEDDINGS';
      case 'INVITATIONS': return 'INVITATIONS';
      default: return 'WEDDING_REGISTRY';
    }
  }

  handleSignup(values) {
    const { onSignup, businessUnit } = this.props;
    const userRequest = values;
    this.setState({ error: false });
    userRequest.business_unit_source = this.mapBusinessUnit(businessUnit);
    onSignup(userRequest)
      .then(() => this.onSignupSuccess())
      .catch(this.handleError);
  }

  handleError(error) {
    this.setState({ error: true, errorMessage: error.detailedMessage });
  }

  render() {
    const { pristine, valid, handleSubmit, businessUnit } = this.props;
    const { error, errorMessage, skipRender } = this.state;

    if (skipRender) return null;

    return (
      <div className="full-page-signup">
        <Helmet
          style={[
            {
              type: 'text/css',
              cssText: ` html, .main-content {
                background: #FCFCFC;
              }
              body {
                margin-bottom: 75px;
              }`
            }
          ]}
        />
        <header className="minimal-header">
          <Link to="/onboard/select-product" className="visible-xs visible-sm"><i className="zolaicon zolaicon-chevron-left" /></Link>
          <a href="/" target="_self">
            <div className="zola-logo-horizontal-black center-block" />
          </a>
        </header>
        <Link to="/onboard/select-product" className="onboard-back-button  hidden-xs hidden-sm">
          <button className="btn btn-sm btn-round btn-secondary">&lsaquo; Back</button>
        </Link>
        <div className="text-h1 intent-title col-xs-12 text-center with-back-button">
          Sign Up
        </div>
        <div className="signup-body">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xs-12 col-sm-6 col-sm-offset-3">
                <form onSubmit={handleSubmit(this.handleSignup)}>
                  <Field
                    className="center-block"
                    name="email"
                    type="email"
                    placeholder="Email"
                    id="signup-email"
                    component={RenderField}
                    required
                    validate={[Validations.REQUIRED, Validations.EMAIL]}
                  />
                  <Field
                    className="center-block"
                    name="password"
                    id="signup-password"
                    type="password"
                    component={RenderField}
                    placeholder="Create Password (8 or more characters)"
                    required
                    validate={[Validations.REQUIRED, Validations.PASSWORD]}
                  />
                  <Field
                    name="timezone"
                    id="signup-timezone"
                    component={RenderHiddenField}
                  />
                  <RenderSubmitButton
                    className="btn-primary btn btn-lg center-block"
                    value="Sign Up For Free"
                    valid={valid}
                    disableWhenPristine
                    pristine={pristine}
                  />
                  <FacebookLoginButton callback={this.onSignupSuccess} errorCallback={this.handleError} businessUnit={this.mapBusinessUnit(businessUnit)} />
                  <div className="clearfix" />
                  { error && (
                    errorMessage ? <div className="form-message-block text-center text-danger">{ errorMessage }</div>
                      : <div className="form-message-block text-center text-danger">There was a problem processing your request. <br />Please try again or contact <a href="https://help.zola.com/hc" target="_blank" rel="noopener noreferrer">customer support</a> for assistance.</div>) }
                  <br />
                </form>
                <div className="signup-footer">
                  <p className="text-center">
                    By creating your Zola account you agree to our <br className="hidden-xs" />
                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                  </p>
                  <p className="text-center margin-top-10">Already a member?</p>
                  <p className="text-center">
                    <Link to="/onboard/login">Log In ›</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <MinimalFooter />
      </div>
    );
  }
}

SignupFullPage.propTypes = {
  location: PropTypes.shape({}),
  handleSubmit: PropTypes.func.isRequired,
  onSignup: PropTypes.func.isRequired,
  businessUnit: PropTypes.oneOf(['REGISTRY', 'WEDDING_WEBSITE', 'INVITATIONS', 'WEDDING_CHECKLIST']),
  ...formProps,
};

const mapStateToProps = state => (
  {
    initialValues: {
      timezone: moment.tz.guess()
    },
    asyncBlurFields: ['email'],
    businessUnit: state.auth.businessUnit,
  }
);

const mapDispatchToProps = dispatch => (
  {
    asyncValidate: ({ email }) => dispatch(checkEmail(email)).then((json) => {
      if (!json.is_available) {
        const error = { email: 'This email address is taken.' };
        throw error;
      }
    }),
    onSignup: createUserRequest => dispatch(signup(createUserRequest)),
    handleIdentifyGuestUser: () => dispatch(identifyGuestUser()),
  }
);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({
  form: 'SignupForm',
  enableReinitialize: true
})(SignupFullPage));



// WEBPACK FOOTER //
// ./src/pages/preauth/SignupPageContainer.jsx